import { Link } from "react-router-dom";
import { MessageSquareHeart } from "lucide-react";
import { SITE_LINKS, DISCLAIMER_SHORT, usePublicConfig } from "@/lib/site";
import { isNativeApp } from "@/lib/platform";
import { useFeedback } from "@/context/FeedbackContext";

const Col = ({ title, links }) => (
  <div>
    <div className="text-[11px] font-mono-ticker uppercase tracking-widest text-slate-500 mb-3">{title}</div>
    <ul className="space-y-2">
      {links.map((l) => (
        <li key={l.to}><Link to={l.to} data-testid={l.tid} className="text-sm text-slate-300 hover:text-cyan-200 transition-colors">{isNativeApp() && l.nativeLabel ? l.nativeLabel : l.label}</Link></li>
      ))}
    </ul>
  </div>
);

export const SiteFooter = ({ compact = false }) => {
  const cfg = usePublicConfig();
  const { openFeedback } = useFeedback();
  const year = new Date().getFullYear();

  if (compact) {
    return (
      <footer className="mt-10 px-4 md:px-6 pb-4 text-[11px] text-slate-500" data-testid="app-footer">
        <div className="border-t border-slate-800/80 pt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          {[...SITE_LINKS.legal, ...SITE_LINKS.product.filter((l) => ["/contact", "/partners", "/transparency"].includes(l.to))].map((l) => (
            <Link key={l.to} to={l.to} data-testid={`${l.tid}-app`} className="hover:text-cyan-200 transition-colors">{l.label}</Link>
          ))}
          <button type="button" onClick={() => openFeedback({ source: "footer" })} data-testid="footer-feedback-btn-app" className="hover:text-cyan-200 transition-colors inline-flex items-center gap-1"><MessageSquareHeart className="w-3 h-3" /> Give Feedback</button>
          <span className="ml-auto">© {year} {cfg.brand}. Research & paper-trading tools — not financial advice.</span>
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t border-slate-800/80 bg-[#060910]" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-12 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
        <div className="space-y-4">
          <img src="/brand/evidex-logo-notag.jpg" alt="EV-IDEX" className="h-12 rounded-lg" />
          <p className="text-sm text-slate-400 leading-relaxed max-w-md">AI-powered cryptocurrency market intelligence: technical analysis, statistical validation, risk insight, AI-assisted research, alerts, verified track record and paper-trading tools.</p>
          <button type="button" onClick={() => openFeedback({ source: "footer" })} data-testid="footer-feedback-btn" className="btn-ghost rounded-xl px-4 py-2.5 text-sm inline-flex items-center gap-2 border border-slate-700">
            <MessageSquareHeart className="w-4 h-4 text-cyan-300" /> Give Feedback
          </button>
        </div>
        <Col title="Company" links={SITE_LINKS.product} />
        <Col title="Legal" links={SITE_LINKS.legal} />
      </div>
      <div className="max-w-6xl mx-auto px-4 md:px-6 pb-10 space-y-3 text-xs text-slate-500 leading-relaxed">
        <p data-testid="footer-disclaimer">{DISCLAIMER_SHORT}</p>
        <p data-testid="footer-legal-line">
          © {year} {cfg.brand}. {cfg.brand} is a brand operated by {cfg.legal_owner}, {cfg.registration}.
          {cfg.contact_email && <> Contact: <a href={`mailto:${cfg.contact_email}`} className="text-slate-300 hover:text-cyan-200" data-testid="footer-contact-email">{cfg.contact_email}</a>.</>}
        </p>
      </div>
    </footer>
  );
};
